<template>
  <div>
    <!-- CRUD principal de Adelantos de Productores -->
    <s-crud title="Reporte de Adelantos de Productores" height="450px" ref="crudAdvanceProducerReport" :filter="filter"
      :config="config" expand-detail sortable searchInput @rowSelected="rowSelected($event)">
      <template v-slot:options>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" small color="success" @click="onExcel()">
              <v-icon small>mdi-file-excel</v-icon>
            </v-btn>
          </template>
          <span>Exportar Reporte Excel</span>
        </v-tooltip>
      </template>
      <!-- Slot de Filtros dentro del s-crud -->
      <template v-slot:filter>
        <v-card class="filter-card mb-3 pa-4" outlined>
          <v-row align="end" dense>
            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-date label="Fecha Desde" v-model="filter.BeginDate" clearable />
            </v-col>
            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-date label="Fecha Hasta" v-model="filter.EndDate" clearable />
            </v-col>
            <!-- Fila 1: Productor, Campaña -->
            <v-col cols="12" sm="6" md="4" class="pb-2">
              <s-select-producer label="Productor" v-model="filter.PrdID" clearable autocomplete />
            </v-col>
            <v-col cols="12" sm="6" md="4" class="pb-2">
              <s-select-campaign-area label="Campaña" v-model="filter.CmaID" clearable noDefault />
            </v-col>
          </v-row>

          <v-row align="end" dense>
            <!-- Fila 2: Fechas, Moneda, Tipo de Adelanto -->

            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-select-definition :def="1129" label="Tipo de Moneda" v-model="filter.TypeCurrency" clearable />
            </v-col>
            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-select-definition :def="1603" label="Tipo de Adelanto" v-model="filter.TypeAdvance" clearable />
            </v-col>

            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-select-definition :def="1173" label="Cultivo" v-model="filter.TypeCultive" clearable />
            </v-col>
            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-select-variety :cultiveID="filter.TypeCultive" label="Variedad" v-model="filter.VrtID" clearable />
            </v-col>
            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-select-definition :def="1172" label="Tipo Cultivo" v-model="filter.TypeCrop" clearable />
            </v-col>
            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-select v-model="filter.StatusBalance" :items="statusBalanceOptions" label="Estado Saldo" clearable />
            </v-col>
          </v-row>

          <v-row align="end" dense>
            <v-col v-if="filter.TypeAdvance == 1" cols="12" sm="6" md="4" class="pb-2">
              <s-article-search v-model="ArtCode" :disabled="viewMode" clearable />
            </v-col>
          </v-row>
        </v-card>
      </template>

      <!-- Slot personalizado para el botón de expansión (flecha) -->
      <template v-slot:[`item.actions_expand`]="{ item }">
        <div class="py-3 px-4 text-center space-x-1">
          <v-btn icon small @click.stop="toggleExpand(item)" color="primary">
            <v-icon>
              {{ isRowExpanded(item) ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
          </v-btn>
          <button @click="" title="Ver Documentos Origen"
            class="bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 p-2 rounded-lg text-xs transition cursor-pointer inline-flex items-center justify-center">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
              </path>
            </svg>
          </button>
          <button @click="" title="Ver Historial de Movimientos"
            class="bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 p-2 rounded-lg text-xs transition cursor-pointer inline-flex items-center justify-center">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z">
              </path>
            </svg>
          </button>
        </div>

      </template>

      <!-- Slot personalizado para el botón de impresión PDF de pagos -->
      <template v-slot:[`item.action_pdf`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              small
              v-on="on"
              color="red darken-2"
              :loading="item.loadingPdf"
              @click.stop="onPrintPaymentPdf(item)"
            >
              <v-icon small>mdi-file-pdf-box</v-icon>
            </v-btn>
          </template>
          <span>Reporte de Pagos del Adelanto</span>
        </v-tooltip>
      </template>

      <!-- Slot personalizado para ID -->
      <template v-slot:[`item.AdpID`]="{ item }">
        <span class="font-weight-bold">#{{ item.AdpID }}</span>
      </template>

      <!-- Slot personalizado para Tipo de Adelanto -->
      <template v-slot:[`item.TypeAdvanceName`]="{ item }">
        <span class="d-inline-flex align-center font-weight-medium">
          <v-icon small class="mr-1" :color="item.TypeAdvanceName === 'Dinero'
            ? 'amber darken-3'
            : 'green darken-2'
            ">
            {{
              item.TypeAdvanceName === "Dinero" ? "mdi-cash-check" : "mdi-leaf"
            }}
          </v-icon>
          {{ item.TypeAdvanceName }}
        </span>
      </template>

      <!-- Slot personalizado para Importe Total -->
      <template v-slot:[`item.TotalAmount`]="{ item }">
        <span class="font-weight-bold primary--text">{{
          formatDecimal(item.TotalAmount)
        }}</span>
      </template>

      <!-- Slot personalizado para Saldo Total -->
      <template v-slot:[`item.TotalBalance`]="{ item }">
        <span class="font-weight-bold green--text text--darken-3">{{
          formatDecimal(item.TotalBalance)
        }}</span>
      </template>

      <!-- Slot personalizado para Importe Total Soles -->
      <template v-slot:[`item.TotalAmountExchangeRate`]="{ item }">
        <span class="font-weight-bold primary--text">{{
          formatDecimal(item.TotalAmountExchangeRate)
        }}</span>
      </template>

      <!-- Slot personalizado para Saldo Total Soles -->
      <template v-slot:[`item.TotalBalanceExchangeRate`]="{ item }">
        <span class="font-weight-bold green--text text--darken-3">{{
          formatDecimal(item.TotalBalanceExchangeRate)
        }}</span>
      </template>

      <!-- Slot personalizado para Estado Saldo -->
      <template v-slot:[`item.StatusBalanceName`]="{ item }">
        <v-chip x-small :color="item.StatusBalanceName &&
          item.StatusBalanceName.toUpperCase().includes('PENDIENTE')
          ? 'orange lighten-4'
          : 'green lighten-4'
          " :class="item.StatusBalanceName &&
            item.StatusBalanceName.toUpperCase().includes('PENDIENTE')
            ? 'orange--text text--darken-4 font-weight-medium'
            : 'green--text text--darken-3 font-weight-medium'
            ">
          {{ item.StatusBalanceName }}
        </v-chip>
      </template>

      <!-- Slot personalizado para Fecha de Aplicación -->
      <template v-slot:[`item.ApplicationDate`]="{ item }">
        <span>{{ formatDate(item.ApplicationDate) }}</span>
      </template>

      <!-- Slot personalizado para Fecha SAP -->
      <template v-slot:[`item.DocDateSAP`]="{ item }">
        <span>{{ formatDate(item.DocDateSAP) }}</span>
      </template>

      <!-- Slot personalizado para Documento SAP -->
      <template v-slot:[`item.DocNumSAP`]="{ item }">
        <span class="font-weight-medium">{{ item.DocNumSAP || '-' }}</span>
      </template>

      <!-- Slot de Fila Expandida provisto por s-crud -->
      <template v-slot:expanded="{ item }">
        <div class="pa-4 bg-expanded-row">
          <v-card class="elevation-1" outlined>
            <!-- Indicador de Carga de Detalles -->
            <v-card-text v-if="item.loadingDetails" class="text-center py-6">
              <v-progress-circular indeterminate color="primary" size="40" width="4"></v-progress-circular>
              <div class="mt-2 text-caption grey--text">
                Cargando información del adelanto...
              </div>
            </v-card-text>

            <!-- Contenido Pestañas una vez cargado -->
            <v-tabs v-else v-model="item.activeTab" color="primary" background-color="grey lighten-4" dense>
              <v-tab>
                <v-icon left small color="green">mdi-map-marker-multiple</v-icon>
                Fundos Asociados ({{ (item.fundos || []).length }})
              </v-tab>
              <v-tab>
                <v-icon left small color="blue">mdi-clipboard-list-outline</v-icon>
                Detalle de Ítems ({{ (item.detalles || []).length }})
              </v-tab>

              <!-- Vista 1: Fundos Asociados -->
              <v-tab-item class="pa-3">
                <v-data-table :headers="headersFarmLots" :items="item.fundos || []" dense hide-default-footer
                  no-data-text="No hay fundos asociados" class="elevation-0">
                  <template v-slot:[`item.FltID`]="{ item: fundo }">
                    <span class="grey--text font-weight-medium">#{{ fundo.FltID }}</span>
                  </template>
                  <template v-slot:[`item.FagName`]="{ item: fundo }">
                    <span class="font-weight-semibold">
                      <v-icon small color="green" class="mr-1">mdi-warehouse</v-icon>
                      {{ fundo.FagName }}
                    </span>
                  </template>
                  <template v-slot:[`item.TypeCultiveText`]="{ item: fundo }">
                    <v-chip x-small color="green lighten-4" class="green--text text--darken-3 font-weight-medium">
                      {{ fundo.TypeCultiveText }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-tab-item>

              <!-- Vista 2: Detalle de Ítems / Artículos -->
              <v-tab-item class="pa-3">
                <v-data-table :headers="headersDetail" :items="item.detalles || []" dense hide-default-footer
                  no-data-text="No hay detalles de ítems" class="elevation-0">
                  <template v-slot:[`item.ItmsGrpNam`]="{ item: detail }">
                    <span class="text-subtitle-2">{{ detail.ItmsGrpNam }}</span>
                  </template>
                  <template v-slot:[`item.Quantity`]="{ item: detail }">
                    <v-chip x-small outlined color="primary" class="font-weight-bold">
                      {{ detail.Quantity }} {{ detail.DedUnit }}
                    </v-chip>
                  </template>
                  <template v-slot:[`item.UnitPrice`]="{ item: detail }">
                    <span class="font-weight-medium">{{
                      formatCurrency(detail.UnitPrice)
                    }}</span>
                  </template>
                  <template v-slot:[`item.Total`]="{ item: detail }">
                    <span class="font-weight-bold primary--text">{{
                      formatCurrency(detail.Total)
                    }}</span>
                  </template>
                </v-data-table>

                <!-- Resumen de Totales del Adelanto -->
                <div class="d-flex justify-end pt-3 pr-4">
                  <div class="text-right">
                    <span class="text-subtitle-2 grey--text">Total Requerido:
                    </span>
                    <span class="text-h6 font-weight-bold primary--text ml-2">
                      {{
                        formatCurrency(
                          calculateTotalAmount(item.detalles || []),
                        )
                      }}
                    </span>
                  </div>
                </div>
              </v-tab-item>
            </v-tabs>
          </v-card>
        </div>
      </template>
    </s-crud>
  </div>
</template>

<script>
import _AdvanceProducerService from "@/services/Technicalassistance/AdvanceProducerService";
import SSelectProducer from "@/components/HarvestSchedule/SSelectProducer";
import SSelectCampaignArea from "@/components/Utils/SSelectCampaignArea.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import SSelectVariety from "@/components/HarvestSchedule/SSelectVariety";
import SSelect from "@/components/Utils/SSelect.vue";
import SArticleSearch from "@/components/Utils/Logistics/SArticleSearch";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  name: "AdvanceProducerReport",
  components: {
    SSelectProducer,
    SSelectCampaignArea,
    SSelectDefinition,
    SSelectVariety,
    SSelect,
    SArticleSearch,
  },
  data() {
    return {
      selectedRow: null,
      localExpanded: [],

      // Filtros vinculados al s-crud
      filter: {
        search: "",
        BeginDate: this.$moment().format("YYYY-MM-DD"),
        EndDate: this.$moment().format("YYYY-MM-DD"),
        PrdID: null,
        CmaID: null,
        TypeCurrency: null,
        TypeAdvance: null,
        TypeCultive: null,
        TypeCrop: null,
        VrtID: null,
        ZonID: null,
        StatusBalance: null,
        ArtCode: null,
      },

      ArtCode: null,
      viewMode: false,

      statusBalanceOptions: [
        { text: "Pendiente", value: 1 },
        { text: "Cancelado", value: 0 },
      ],

      // Cabeceras internas de las pestañas expandibles
      headersFarmLots: [
        { text: "ID Lote", value: "FltID", sortable: false },
        { text: "Fundo", value: "FagName", sortable: false },
        { text: "Lote", value: "FltName", sortable: false },
        {
          text: "Responsable de Cobro",
          value: "ResponsibleCollectingFarmLot",
          sortable: false,
        },
        { text: "Tipo de Cultivo", value: "TypeCultiveText", sortable: false },
        { text: "Cultivo", value: "TypeCropText", sortable: false },
        { text: "Variedad", value: "VrtIDText", sortable: false },
        { text: "Zona", value: "ZonIDText", sortable: false },
      ],
      headersDetail: [
        { text: "Categoría", value: "ItmsGrpNam", sortable: false },
        { text: "Descripción Artículo", value: "Description", sortable: false },
        {
          text: "Cantidad Requerida",
          value: "Quantity",
          sortable: false,
          align: "center",
        },
        {
          text: "Precio Unitario",
          value: "UnitPrice",
          sortable: false,
          align: "right",
        },
        {
          text: "Importe Total",
          value: "Total",
          sortable: false,
          align: "right",
        },
      ],

      // Configuración requerida por el s-crud
      config: {
        model: {
          AdpID: "ID",
          ApplicationDate: "datetime",
          SecUpdate: "datetime",
        },
        // Enlazamos con el servicio de backend usando el endpoint específico
        service: {
          pagination: (parameters, requestID) => {
            return _AdvanceProducerService.PaginationReport(
              parameters,
              requestID,
            );
          },
        },
        headers: [
          {
            text: "",
            value: "actions_expand",
            sortable: false,
            align: "center",
            width: 50,
          },
          {
            text: "",
            value: "action_pdf",
            sortable: false,
            align: "center",
            width: 50,
          },
          { text: "ID", value: "AdpID", sortable: false, width: 60 },
          {
            text: "N° Documento",
            value: "AdpDocumentNumber",
            sortable: false,
            width: 120,
          },
          {
            text: "Fecha",
            value: "ApplicationDate",
            sortable: false,
            width: 100,
            align: "center",
          },
          {
            text: "Adelanto",
            value: "TypeAdvanceName",
            sortable: false,
            width: 100,
            align: "center",
          },
          {
            text: "Campaña",
            value: "CmaName",
            sortable: false,
            width: 150,
            align: "center",
          },
          {
            text: "Productor",
            value: "ProducerName",
            sortable: false,
            width: 250,
            align: "center",
          },
          {
            text: "Cód. SAP",
            value: "PrdCardCode",
            sortable: false,
            width: 90,
            align: "center",
          },
          {
            text: "RUC / DNI",
            value: "DocumentNumberProducer",
            sortable: false,
            width: 100,
            align: "center",
          },
          {
            text: "Moneda",
            value: "TypeCurrencyName",
            sortable: false,
            width: 100,
            align: "center",
          },
          {
            text: "Tipo de Cambio",
            value: "ExchangeRate",
            sortable: false,
            width: 100,
            align: "center",
          },
          {
            text: "Importe Total",
            value: "TotalAmount",
            sortable: false,
            width: 110,
            align: "right",
          },
          {
            text: "Saldo Total",
            value: "TotalBalance",
            sortable: false,
            width: 110,
            align: "right",
          },
          {
            text: "Importe Total Soles",
            value: "TotalAmountExchangeRate",
            sortable: false,
            width: 110,
            align: "right",
          },
          {
            text: "Saldo Total Soles",
            value: "TotalBalanceExchangeRate",
            sortable: false,
            width: 110,
            align: "right",
          },
          {
            text: "Estado",
            value: "StatusBalanceName",
            sortable: false,
            width: 110,
            align: "center",
          },
          {
            text: "Fecha SAP",
            value: "DocDateSAP",
            sortable: false,
            width: 100,
            align: "center",
          },
          {
            text: "Documento SAP",
            value: "DocNumSAP",
            sortable: false,
            width: 130,
            align: "center",
          },
          {
            text: "Justificación",
            value: "AdpJustification",
            sortable: false,
            width: 220,
          },
          {
            text: "Observación",
            value: "Observation",
            sortable: false,
            width: 220,
          },
        ],
      },
    };
  },
  watch: {
    "filter.TypeCultive"(newVal) {
      this.filter.VrtID = null;
    },
    "filter.TypeAdvance"(newVal) {
      if (newVal != 1) {
        this.ArtCode = null;
      }
    },
    ArtCode(newVal) {
      this.filter.ArtCode = newVal ? newVal.ArtCode : null;
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return "";
      if (typeof value === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}/.test(value.trim())) {
        return value;
      }
      const m = this.$moment(value);
      return m.isValid() ? m.format("DD/MM/YYYY") : String(value);
    },
    formatCurrency(value) {
      if (value == null || isNaN(value)) return "S/ 0.00";
      return `S/ ${Number(value).toLocaleString("es-PE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    formatDecimal(value) {
      if (value == null || isNaN(value)) return "0.00";
      return Number(value).toLocaleString("es-PE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    calculateTotalAmount(detalles) {
      return detalles.reduce((sum, current) => sum + (current.Total || 0), 0);
    },
    rowSelected(selection) {
      this.selectedRow =
        selection && selection.length > 0 ? selection[0] : null;
      const crud = this.$refs.crudAdvanceProducerReport;
      if (crud && crud.expanded) {
        this.localExpanded = [...crud.expanded];
      } else {
        this.localExpanded = [];
      }
    },
    async toggleExpand(item) {
      const crud = this.$refs.crudAdvanceProducerReport;
      if (!crud) return;
      const idx = this.localExpanded.indexOf(item);
      if (idx > -1) {
        // Colapsar
        this.localExpanded.splice(idx, 1);
        crud.expanded = crud.expanded.filter((i) => i.AdpID !== item.AdpID);
      } else {
        // Expandir
        this.localExpanded = [item];
        crud.expanded = [item];

        // Carga bajo demanda si no se han cargado previamente
        if (!item.loadedDetails) {
          this.$set(item, "loadingDetails", true);
          this.$set(item, "fundos", []);
          this.$set(item, "detalles", []);

          const requestID = this.$fun.getUserID();
          const parametersPagination = {
            filter: {},
            draw: 1,
            start: 0,
            length: 100,
            order: [{ dir: "asc", column: "" }],
            searchText: "",
          };

          try {
            const [resFundos, resDetails] = await Promise.all([
              _AdvanceProducerService.getAdvanceProducerFarmLots(
                parametersPagination,
                item.AdpID,
                requestID,
              ),
              _AdvanceProducerService.getAdvanceProducerDetails(
                parametersPagination,
                item.AdpID,
                requestID,
              ),
            ]);

            if (resFundos.status === 200) {
              const fundosData = resFundos.data.data || resFundos.data || [];
              this.$set(item, "fundos", fundosData);
            }
            if (resDetails.status === 200) {
              const detallesData =
                resDetails.data.data || resDetails.data || [];
              this.$set(item, "detalles", detallesData);
            }
            this.$set(item, "loadedDetails", true);
            this.$set(item, "activeTab", 0);
          } catch (error) {
            console.error("Error al cargar detalles:", error);
            this.$fun.alert(
              "Error al cargar los detalles del adelanto",
              "error",
            );
          } finally {
            this.$set(item, "loadingDetails", false);
          }
        }
      }
    },
    isRowExpanded(item) {
      return this.localExpanded.includes(item);
    },
    onAdd() {
      this.$fun.alert("Simulando Acción: Nuevo Registro", "info");
    },
    onEdit() {
      if (!this.selectedRow) {
        this.$fun.alert("Seleccione una fila para editar", "warning");
        return;
      }
      this.$fun.alert(
        `Simulando Edición del adelanto: #${this.selectedRow.AdpID}`,
        "info",
      );
    },
    onDelete() {
      if (!this.selectedRow) {
        this.$fun.alert("Seleccione una fila para eliminar", "warning");
        return;
      }
      this.$fun.alert(
        `Simulando Eliminación del adelanto: #${this.selectedRow.AdpID}`,
        "question",
      );
    },
    async onPrintPaymentPdf(item) {
      this.$set(item, "loadingPdf", true);
      try {
        const requestID = this.$fun.getUserID();
        // Si IsDirectSAP=1 el registro viene solo de SAP: enviamos AdpID como DocEntry
        // Si IsDirectSAP=0 el registro tiene ID propio del sistema: enviamos AdpID como AdpID
        const isDirectSAP = item.IsDirectSAP === 1 || item.IsDirectSAP === true;
        const docEntry = isDirectSAP ? item.AdpID : 0;
        const adpID    = isDirectSAP ? 0 : item.AdpID;
        // Para registros de SAP directo los campos del sistema son null,
        // por eso el PrdID se obtiene del filtro activo
        const prdID    = item.PrdID || this.filter.PrdID || 0;

        const isMoneyAdvance = item.TypeAdvance === 2 || item.TypeAdvanceName === "Dinero";

        let response;
        if (isMoneyAdvance) {
          response = await _AdvanceProducerService.SupplyMoneyAdvancePaymentReport(
            prdID, adpID, docEntry, requestID
          );
        } else {
          response = await _AdvanceProducerService.SupplyAdvancePaymentReport(
            null, null, prdID, adpID, docEntry, requestID
          );
        }

        if (!response || response.status !== 200) {
          this.$fun.alert("Error al obtener el reporte de pagos.", "error");
          return;
        }

        // El backend puede devolver un array directo o un objeto paginado { data: [...] }
        const rows = Array.isArray(response.data)
          ? response.data
          : (response.data && Array.isArray(response.data.data) ? response.data.data : []);
        if (!rows.length) {
          this.$fun.alert("No se encontraron registros de pago para este adelanto.", "warning");
          return;
        }

        if (isMoneyAdvance) {
          this.generateMoneyPaymentPdf(item, rows);
        } else {
          this.generatePaymentPdf(item, rows);
        }
      } catch (error) {
        console.error("Error al generar PDF de pagos:", error);
        this.$fun.alert("Ocurrió un error al generar el PDF.", "error");
      } finally {
        this.$set(item, "loadingPdf", false);
      }
    },

    generateMoneyPaymentPdf(item, rows) {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();   // 297mm
      const pageH = doc.internal.pageSize.getHeight();  // 210mm
      const ml = 10;

      const fmt = (v) => {
        if (v == null || isNaN(v)) return "0.00";
        return Number(v).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      };

      const first   = rows[0] || {};
      const lastRow = rows[rows.length - 1] || {};

      const rawProducerName = item.ProducerName || first.ProducerName || (first.VendorName && first.VendorName !== '-' ? first.VendorName : null) || first.FagName || "-";
      const producerName    = String(rawProducerName || "-").toUpperCase();
      const producerDoc     = String(item.DocumentNumberProducer || first.ProducerDocumentNumber || (first.VendorCode && first.VendorCode !== '-' ? first.VendorCode : null) || "-");
      const campaignName    = String(item.CmaName || first.CmaName || "-");
      const docNumber       = String(item.AdpDocumentNumber || first.AdpDocumentNumber || "-");
      const appDate         = this.formatDate(item.ApplicationDate || first.ApplicationDate || first.PaymentDate) || "-";
      const currency        = String(first.DocumentCurrency || item.TypeCurrencyName || "SOL");

      const farmName      = String(first.FagName || "-");
      const lotName       = first.FltName != null ? String(first.FltName) : "-";
      const zoneName      = String(first.ZonIDText || "-");
      const cropText      = String(first.TypeCultiveText || "-");
      const varietyText   = String(first.VrtIDText || "-");
      const hectares      = first.FltHectares != null ? String(first.FltHectares) : "-";
      const plants        = first.FltPlants != null ? String(first.FltPlants) : "-";
      const volume        = first.FltVolume != null ? String(first.FltVolume) : "-";

      const totalGrossPEN   = rows.reduce((s, r) => s + (parseFloat(r.GrossAmountPEN) || 0), 0);
      const totalAppliedPEN = rows.reduce((s, r) => s + (parseFloat(r.AppliedAmountPEN) || 0), 0);
      const pendingPEN      = parseFloat(lastRow.OutstandingBalancePEN) || 0;

      const drawPageFrame = (pageNum, totalPgs) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text("SUNSHINE EXPORT S.A.C", ml, 10);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(60, 60, 60);
        doc.text("Carretera TamboGrande las Lomas Km 1077 + 179 Mts", ml, 14);
        doc.text("RUC: 20329725431", ml, 17.5);
        doc.text("Área: ASISTENCIA TÉCNICA / ADELANTO DE DINERO", ml, 21);

        const boxW = 95;
        const boxX = pageW - ml - boxW;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.rect(boxX, 7, boxW, 16);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text("ESTADO DE CUENTA - ADELANTO DE DINERO", boxX + boxW / 2, 12, { align: "center" });

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text(`N° Adelanto: ${docNumber}`, boxX + boxW / 2, 16.5, { align: "center" });
        const now = this.$moment().format("DD/MM/YYYY HH:mm");
        doc.setFontSize(7);
        doc.text(`Fecha Impresión: ${now}`, boxX + boxW / 2, 20.5, { align: "center" });

        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.2);
        doc.line(ml, pageH - 8, pageW - ml, pageH - 8);

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);
        doc.text("SUNSHINE EXPORT S.A.C - Sistema SunExpert", ml, pageH - 4);
        doc.text(`Página ${pageNum} de ${totalPgs}`, pageW - ml, pageH - 4, { align: "right" });
      };

      let y = 26;

      const infoW = pageW - ml * 2;
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.rect(ml, y, infoW, 26);

      doc.setFillColor(235, 235, 235);
      doc.rect(ml, y, infoW, 5.5, "F");
      doc.line(ml, y + 5.5, ml + infoW, y + 5.5);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`DATOS DEL PRODUCTOR: ${producerName.toUpperCase()} (RUC/DNI/CÓD: ${producerDoc})`, ml + 3, y + 4);

      doc.setFontSize(7);
      const col1 = ml + 3;
      const col2 = ml + 72;
      const col3 = ml + 140;
      const col4 = ml + 210;

      const rowY1 = y + 10.5;
      const rowY2 = y + 16.5;
      const rowY3 = y + 22.5;

      // Fila 1
      doc.setFont("helvetica", "bold"); doc.text("Campaña:", col1, rowY1);
      doc.setFont("helvetica", "normal"); doc.text(String(campaignName), col1 + 18, rowY1);

      doc.setFont("helvetica", "bold"); doc.text("N° Doc. Adelanto:", col2, rowY1);
      doc.setFont("helvetica", "normal"); doc.text(String(docNumber), col2 + 28, rowY1);

      doc.setFont("helvetica", "bold"); doc.text("Fecha Aplicación:", col3, rowY1);
      doc.setFont("helvetica", "normal"); doc.text(String(appDate), col3 + 26, rowY1);

      doc.setFont("helvetica", "bold"); doc.text("Moneda:", col4, rowY1);
      doc.setFont("helvetica", "normal"); doc.text(String(currency), col4 + 14, rowY1);

      doc.setDrawColor(230, 230, 230);
      doc.line(ml + 1, y + 12.5, ml + infoW - 1, y + 12.5);

      // Fila 2
      doc.setFont("helvetica", "bold"); doc.text("Fundo / Lote:", col1, rowY2);
      doc.setFont("helvetica", "normal"); doc.text(`${farmName} / Lote ${lotName}`, col1 + 22, rowY2);

      doc.setFont("helvetica", "bold"); doc.text("Zona / Cultivo:", col2, rowY2);
      doc.setFont("helvetica", "normal"); doc.text(`${zoneName} - ${cropText} (${varietyText})`, col2 + 24, rowY2);

      doc.setFont("helvetica", "bold"); doc.text("Hectáreas / Plantas:", col3, rowY2);
      doc.setFont("helvetica", "normal"); doc.text(`${hectares} Ha / ${plants} Plantas`, col3 + 30, rowY2);

      doc.setFont("helvetica", "bold"); doc.text("Volumen Est. (Jabas):", col4, rowY2);
      doc.setFont("helvetica", "normal"); doc.text(String(volume), col4 + 32, rowY2);

      doc.line(ml + 1, y + 18.5, ml + infoW - 1, y + 18.5);

      // Fila 3
      doc.setFont("helvetica", "bold"); doc.text("Importe Bruto Total:", col1, rowY3);
      doc.setFont("helvetica", "normal"); doc.text(`S/ ${fmt(totalGrossPEN)}`, col1 + 30, rowY3);

      doc.setFont("helvetica", "bold"); doc.text("Importe Aplicado Total:", col2, rowY3);
      doc.setFont("helvetica", "normal"); doc.text(`S/ ${fmt(totalAppliedPEN)}`, col2 + 34, rowY3);

      doc.setFont("helvetica", "bold"); doc.text("Saldo Pendiente Actual:", col3, rowY3);
      doc.setFont("helvetica", "normal"); doc.text(`S/ ${fmt(pendingPEN)}`, col3 + 34, rowY3);

      y += 29;

      const tableColumns = [
        { header: "F. Conciliación",      dataKey: "ReconciliationDate" },
        { header: "Tipo Transacción",     dataKey: "TransactionType" },
        { header: "Doc. Aplicado",        dataKey: "AppliedDocuments" },
        { header: "Pagado/Aplicado (S/)", dataKey: "AppliedAmountPEN" },
        { header: "Saldo Pendiente (S/)", dataKey: "OutstandingBalancePEN" },
      ];

      const tableRows = rows.map((r) => ({
        ReconciliationDate:    r.ReconciliationDate     || "-",
        TransactionType:       r.TransactionType        || "-",
        AppliedDocuments:      r.AppliedDocuments       || "-",
        AppliedAmountPEN:      r.AppliedAmountPEN      != null ? fmt(r.AppliedAmountPEN)      : "-",
        OutstandingBalancePEN: r.OutstandingBalancePEN != null ? fmt(r.OutstandingBalancePEN) : "-",
      }));

      doc.autoTable({
        startY: y,
        columns: tableColumns,
        body: tableRows,
        margin: { left: ml, right: ml, bottom: 12 },
        tableWidth: pageW - ml * 2,
        styles: {
          fontSize: 6.5,
          cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 },
          lineColor: [100, 100, 100],
          lineWidth: 0.15,
          font: "helvetica",
          textColor: [0, 0, 0],
          valign: "middle",
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [230, 230, 230],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          fontSize: 6.5,
          halign: "center",
          lineColor: [0, 0, 0],
          lineWidth: 0.25,
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255],
        },
        columnStyles: {
          0: { halign: "center", cellWidth: 28 },   // F. Conciliación
          1: { cellWidth: 70,    overflow: "linebreak" }, // Tipo Transacción
          2: { cellWidth: 85,    overflow: "linebreak" }, // Doc. Aplicado
          3: { halign: "right",  cellWidth: 47 },   // Pagado/Aplicado
          4: { halign: "right",  cellWidth: 47 },   // Saldo Pendiente
        },
        foot: [[
          { content: "TOTALES", colSpan: 3, styles: { halign: "right", fontStyle: "bold", fillColor: [240, 240, 240], textColor: [0,0,0] } },
          { content: `S/ ${fmt(totalAppliedPEN)}`, styles: { halign: "right", fontStyle: "bold", fillColor: [240, 240, 240], textColor: [0,0,0] } },
          { content: `S/ ${fmt(pendingPEN)}`, styles: { halign: "right", fontStyle: "bold", fillColor: [240, 240, 240], textColor: [0,0,0] } },
        ]],
        showFoot: "lastPage",
      });

      const totalPages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        drawPageFrame(p, totalPages);
      }

      const fileName = `EstadoCuenta_AdelantoDinero_${item.AdpID || item.DocNumSAP || "SAP"}_${this.$moment().format("YYYYMMDD_HHmm")}.pdf`;
      doc.save(fileName);
    },

    generatePaymentPdf(item, rows) {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();   // 297mm
      const pageH = doc.internal.pageSize.getHeight();  // 210mm
      const ml = 10; // Margen de 10mm para maximizar espacio disponible (277mm de ancho utilizable)

      const fmt = (v) =>
        Number(v).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      const first   = rows[0] || {};
      const lastRow = rows[rows.length - 1] || {};

      const rawProducerName = item.ProducerName || first.CustomerName || first.ProducerName || "-";
      const producerName    = String(rawProducerName || "-").toUpperCase();
      const producerDoc     = String(item.DocumentNumberProducer || first.ProducerDocumentNumber || "-");
      const campaignName    = String(item.CmaName || first.CmaName || "-");
      const docNumber       = String(item.AdpDocumentNumber || first.AdpDocumentNumber || "-");
      const appDate         = this.formatDate(item.ApplicationDate || first.ApplicationDate) || "-";
      const invoiceNum      = String(first.InvoiceNumber || "-");
      const currency        = String(first.DocumentCurrency || item.TypeCurrencyName || "-");
      const exchangeRate    = first.InvoiceExchangeRate != null ? Number(first.InvoiceExchangeRate).toFixed(4) : "-";

      const grossUSD      = parseFloat(first.GrossAmountUSD) || 0;
      const grossPEN      = parseFloat(first.GrossAmountPEN) || 0;
      const totalPaidUSD  = rows.reduce((s, r) => s + (parseFloat(r.CashReceivedUSD) || 0), 0);
      const totalPaidPEN  = rows.reduce((s, r) => s + (parseFloat(r.CashReceivedPEN) || 0), 0);
      const pendingUSD    = parseFloat(lastRow.OutstandingBalanceUSD) || 0;
      const pendingPEN    = parseFloat(lastRow.OutstandingBalancePEN) || 0;

      const drawPageFrame = (pageNum, totalPgs) => {
        // ── ENCABEZADO ESTILO BOLETA DE PAGO EMPRESARIAL ────────────────
        // Recuadro superior izquierdo: Datos Empresa
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text("SUNSHINE EXPORT S.A.C", ml, 10);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(60, 60, 60);
        doc.text("Carretera TamboGrande las Lomas Km 1077 + 179 Mts", ml, 14);
        doc.text("RUC: 20329725431", ml, 17.5);
        doc.text("Área: ASISTENCIA TÉCNICA / ADELANTO DE INSUMOS", ml, 21);

        // Recuadro superior derecho: Título del Documento
        const boxW = 85;
        const boxX = pageW - ml - boxW;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.rect(boxX, 7, boxW, 16);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.5);
        doc.setTextColor(0, 0, 0);
        doc.text("REPORTE DE ESTADO DE CUENTA", boxX + boxW / 2, 12, { align: "center" });

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text(`N° Adelanto: ${docNumber}`, boxX + boxW / 2, 16.5, { align: "center" });
        const now = this.$moment().format("DD/MM/YYYY HH:mm");
        doc.setFontSize(7);
        doc.text(`Fecha Impresión: ${now}`, boxX + boxW / 2, 20.5, { align: "center" });

        // ── PIE DE PÁGINA ──────────────────────────────────────────────
        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.2);
        doc.line(ml, pageH - 8, pageW - ml, pageH - 8);

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);
        doc.text("SUNSHINE EXPORT S.A.C - Sistema SunExpert", ml, pageH - 4);
        doc.text(`Página ${pageNum} de ${totalPgs}`, pageW - ml, pageH - 4, { align: "right" });
      };

      let y = 26;

      // ── RECUADRO DE DATOS DEL PRODUCTOR Y ADELANTO ───────────────────
      const infoW = pageW - ml * 2;
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.rect(ml, y, infoW, 22);

      // Cabecera del recuadro
      doc.setFillColor(235, 235, 235);
      doc.rect(ml, y, infoW, 5.5, "F");
      doc.line(ml, y + 5.5, ml + infoW, y + 5.5);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`DATOS DEL PRODUCTOR: ${producerName} (RUC/DNI: ${producerDoc})`, ml + 3, y + 4);

      // Grid interior de datos
      doc.setFontSize(7);
      const col1 = ml + 3;
      const col2 = ml + 72;
      const col3 = ml + 140;
      const col4 = ml + 210;

      const rowY1 = y + 10.5;
      const rowY2 = y + 17;

      // Fila 1
      doc.setFont("helvetica", "bold"); doc.text("Campaña:", col1, rowY1);
      doc.setFont("helvetica", "normal"); doc.text(String(campaignName), col1 + 18, rowY1);

      doc.setFont("helvetica", "bold"); doc.text("N° Doc. Adelanto:", col2, rowY1);
      doc.setFont("helvetica", "normal"); doc.text(String(docNumber), col2 + 28, rowY1);

      doc.setFont("helvetica", "bold"); doc.text("Fecha Aplicación:", col3, rowY1);
      doc.setFont("helvetica", "normal"); doc.text(String(appDate), col3 + 26, rowY1);

      doc.setFont("helvetica", "bold"); doc.text("Moneda:", col4, rowY1);
      doc.setFont("helvetica", "normal"); doc.text(String(currency), col4 + 14, rowY1);

      // Línea divisoria horizontal
      doc.setDrawColor(220, 220, 220);
      doc.line(ml + 1, y + 12.5, ml + infoW - 1, y + 12.5);

      // Fila 2
      doc.setFont("helvetica", "bold"); doc.text("Factura SAP:", col1, rowY2);
      doc.setFont("helvetica", "normal"); doc.text(String(invoiceNum), col1 + 20, rowY2);

      doc.setFont("helvetica", "bold"); doc.text("Tipo de Cambio:", col2, rowY2);
      doc.setFont("helvetica", "normal"); doc.text(String(exchangeRate), col2 + 24, rowY2);

      doc.setFont("helvetica", "bold"); doc.text("Imp. Bruto USD / PEN:", col3, rowY2);
      doc.setFont("helvetica", "normal"); doc.text(`$ ${fmt(grossUSD)} / S/ ${fmt(grossPEN)}`, col3 + 32, rowY2);

      doc.setFont("helvetica", "bold"); doc.text("Saldo USD / PEN:", col4, rowY2);
      doc.setFont("helvetica", "normal"); doc.text(`$ ${fmt(pendingUSD)} / S/ ${fmt(pendingPEN)}`, col4 + 24, rowY2);

      y += 25;

      // ── TABLA DE DETALLE DE PAGOS ────────────────────────────────────
      const tableColumns = [
        { header: "F. Concil.",      dataKey: "ReconciliationDate" },
        { header: "Mon.",            dataKey: "DocumentCurrency" },
        { header: "T.C.",            dataKey: "PaymentExchangeRate" },
        { header: "Tipo Pago",       dataKey: "TransactionType" },
        { header: "Doc. Aplicado",   dataKey: "AppliedDocuments" },
        { header: "Pagado USD",      dataKey: "CashReceivedUSD" },
        { header: "Pagado PEN",      dataKey: "CashReceivedPEN" },
        { header: "Saldo USD",       dataKey: "OutstandingBalanceUSD" },
        { header: "Saldo PEN",       dataKey: "OutstandingBalancePEN" },
      ];

      const tableRows = rows.map((r) => ({
        ReconciliationDate:    r.ReconciliationDate     || "-",
        DocumentCurrency:      r.DocumentCurrency       || "-",
        PaymentExchangeRate:   r.PaymentExchangeRate != null ? Number(r.PaymentExchangeRate).toFixed(4) : "-",
        TransactionType:       r.TransactionType        || "-",
        AppliedDocuments:      r.AppliedDocuments       || "-",
        CashReceivedUSD:       r.CashReceivedUSD       != null ? fmt(r.CashReceivedUSD)       : "-",
        CashReceivedPEN:       r.CashReceivedPEN       != null ? fmt(r.CashReceivedPEN)       : "-",
        OutstandingBalanceUSD: r.OutstandingBalanceUSD != null ? fmt(r.OutstandingBalanceUSD) : "-",
        OutstandingBalancePEN: r.OutstandingBalancePEN != null ? fmt(r.OutstandingBalancePEN) : "-",
      }));

      doc.autoTable({
        startY: y,
        columns: tableColumns,
        body: tableRows,
        margin: { left: ml, right: ml, bottom: 12 },
        tableWidth: pageW - ml * 2, // 277mm exactamente de ancho
        styles: {
          fontSize: 7,
          cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 },
          lineColor: [100, 100, 100],
          lineWidth: 0.15,
          font: "helvetica",
          textColor: [0, 0, 0],
          valign: "middle",
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [230, 230, 230],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          fontSize: 7,
          halign: "center",
          lineColor: [0, 0, 0],
          lineWidth: 0.25,
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255],
        },
        columnStyles: {
          0: { halign: "center", cellWidth: 24 },   // F. Concil.
          1: { halign: "center", cellWidth: 15 },   // Moneda
          2: { halign: "right",  cellWidth: 20 },   // T.C.
          3: { cellWidth: 42,    overflow: "linebreak" }, // Tipo Pago
          4: { cellWidth: 56,    overflow: "linebreak" }, // Doc. Aplicado
          5: { halign: "right",  cellWidth: 30 },   // Pagado USD
          6: { halign: "right",  cellWidth: 30 },   // Pagado PEN
          7: { halign: "right",  cellWidth: 30 },   // Saldo USD
          8: { halign: "right",  cellWidth: 30 },   // Saldo PEN
        },
        foot: [[
          { content: "TOTALES", colSpan: 5, styles: { halign: "right", fontStyle: "bold", fillColor: [240, 240, 240], textColor: [0,0,0] } },
          { content: `$ ${fmt(totalPaidUSD)}`,  styles: { halign: "right", fontStyle: "bold", fillColor: [240, 240, 240], textColor: [0,0,0] } },
          { content: `S/ ${fmt(totalPaidPEN)}`, styles: { halign: "right", fontStyle: "bold", fillColor: [240, 240, 240], textColor: [0,0,0] } },
          { content: `$ ${fmt(pendingUSD)}`,  styles: { halign: "right", fontStyle: "bold", fillColor: [240, 240, 240], textColor: [0,0,0] } },
          { content: `S/ ${fmt(pendingPEN)}`, styles: { halign: "right", fontStyle: "bold", fillColor: [240, 240, 240], textColor: [0,0,0] } },
        ]],
        showFoot: "lastPage",
      });

      const totalPages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        drawPageFrame(p, totalPages);
      }

      const fileName = `EstadoCuenta_Adelanto_${item.AdpID || item.DocNumSAP || "SAP"}_${this.$moment().format("YYYYMMDD_HHmm")}.pdf`;
      doc.save(fileName);
    },

    async onExcel() {
      try {
        this.$fun.alert("Generando Excel de reporte...", "info");
        const params = {
          filter: { ...this.filter },
          draw: 1,
          start: 0,
          length: -1, // Retrieve all records
          searchText: "",
          order: []
        };
        const response = await _AdvanceProducerService.PaginationReport(params, this.$fun.getUserID());
        if (!response || response.status !== 200 || !response.data || !response.data.data) {
          this.$fun.alert("Ocurrió un error al obtener los datos para la exportación.", "error");
          return;
        }
        const list = response.data.data;
        if (list.length === 0) {
          this.$fun.alert("No hay registros que coincidan con los filtros aplicados.", "warning");
          return;
        }

        const workbook = new ExcelJS.Workbook();
        const sheetName = "Adelantos de Productores";
        const worksheet = workbook.addWorksheet(sheetName, {
          properties: { tabColor: { argb: "FF1F4E78" } }
        });

        // Column structures
        worksheet.columns = [
          { header: "ID", key: "AdpID", width: 10 },
          { header: "N° Documento", key: "AdpDocumentNumber", width: 18 },
          { header: "Fecha", key: "ApplicationDate", width: 15 },
          { header: "Tipo Adelanto", key: "TypeAdvanceName", width: 18 },
          { header: "Campaña", key: "CmaName", width: 20 },
          { header: "Productor", key: "ProducerName", width: 30 },
          { header: "Cód. SAP", key: "PrdCardCode", width: 15 },
          { header: "RUC / DNI", key: "DocumentNumberProducer", width: 15 },
          { header: "Moneda", key: "TypeCurrencyName", width: 12 },
          { header: "Tipo de Cambio", key: "ExchangeRate", width: 15 },
          { header: "Importe Total", key: "TotalAmount", width: 15 },
          { header: "Saldo Total", key: "TotalBalance", width: 15 },
          { header: "Importe Total Soles", key: "TotalAmountExchangeRate", width: 18 },
          { header: "Saldo Total Soles", key: "TotalBalanceExchangeRate", width: 18 },
          { header: "Estado", key: "StatusBalanceName", width: 15 },
          { header: "Fecha SAP", key: "DocDateSAP", width: 15 },
          { header: "Documento SAP", key: "DocNumSAP", width: 18 },
          { header: "Justificación", key: "AdpJustification", width: 30 },
          { header: "Observación", key: "Observation", width: 30 },
          { header: "Lotes", key: "FltIDS", width: 25 }
        ];

        // Format headers
        const headerRow = worksheet.getRow(1);
        headerRow.height = 28;
        headerRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF1F4E78" }
          };
          cell.font = {
            name: "Calibri",
            size: 10,
            bold: true,
            color: { argb: "FFFFFFFF" }
          };
          cell.alignment = {
            vertical: "middle",
            horizontal: "center",
            wrapText: true
          };
          cell.border = {
            top: { style: "thin", color: { argb: "FFD3D3D3" } },
            left: { style: "thin", color: { argb: "FFD3D3D3" } },
            bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
            right: { style: "thin", color: { argb: "FFD3D3D3" } }
          };
        });

        // Add records
        list.forEach((item) => {
          const rowData = {
            AdpID: item.AdpID,
            AdpDocumentNumber: item.AdpDocumentNumber,
            ApplicationDate: item.ApplicationDate ? this.$moment(item.ApplicationDate).format("DD/MM/YYYY") : "",
            TypeAdvanceName: item.TypeAdvanceName,
            CmaName: item.CmaName,
            ProducerName: item.ProducerName,
            PrdCardCode: item.PrdCardCode,
            DocumentNumberProducer: item.DocumentNumberProducer,
            TypeCurrencyName: item.TypeCurrencyName,
            ExchangeRate: item.ExchangeRate != null ? Number(item.ExchangeRate) : null,
            TotalAmount: item.TotalAmount != null ? Number(item.TotalAmount) : null,
            TotalBalance: item.TotalBalance != null ? Number(item.TotalBalance) : null,
            TotalAmountExchangeRate: item.TotalAmountExchangeRate != null ? Number(item.TotalAmountExchangeRate) : null,
            TotalBalanceExchangeRate: item.TotalBalanceExchangeRate != null ? Number(item.TotalBalanceExchangeRate) : null,
            StatusBalanceName: item.StatusBalanceName,
            DocDateSAP: item.DocDateSAP ? this.$moment(item.DocDateSAP).format("DD/MM/YYYY") : "",
            DocNumSAP: item.DocNumSAP,
            AdpJustification: item.AdpJustification,
            Observation: item.Observation,
            FltIDS: item.FltIDS
          };

          const row = worksheet.addRow(rowData);
          row.height = 20;

          row.eachCell((cell, colIdx) => {
            cell.font = { name: "Calibri", size: 9 };
            cell.border = {
              top: { style: "thin", color: { argb: "FFE0E0E0" } },
              left: { style: "thin", color: { argb: "FFE0E0E0" } },
              bottom: { style: "thin", color: { argb: "FFE0E0E0" } },
              right: { style: "thin", color: { argb: "FFE0E0E0" } }
            };

            // ID, N° Documento, Cód. SAP, RUC / DNI, Moneda, Estado
            if ([1, 2, 7, 8, 9, 15].includes(colIdx)) {
              cell.alignment = { vertical: "middle", horizontal: "center" };
            } else if (colIdx === 3) {
              // Date
              cell.alignment = { vertical: "middle", horizontal: "center" };
            } else if ([10, 11, 12, 13, 14].includes(colIdx)) {
              // Numeric columns
              cell.numFmt = "#,##0.00";
              cell.alignment = { vertical: "middle", horizontal: "right" };
            } else {
              cell.alignment = { vertical: "middle", horizontal: "left" };
            }
          });
        });

        // Add total summary row
        const totalsRow = worksheet.addRow([]);
        totalsRow.height = 24;
        const labelCell = totalsRow.getCell(1);
        labelCell.value = "TOTAL GENERAL";
        labelCell.font = { name: "Calibri", size: 9, bold: true };
        labelCell.alignment = { vertical: "middle", horizontal: "left" };

        const colLetter = (idx) => {
          let temp = idx;
          let letter = "";
          while (temp > 0) {
            let modulo = (temp - 1) % 26;
            letter = String.fromCharCode(65 + modulo) + letter;
            temp = Math.floor((temp - modulo) / 26);
          }
          return letter;
        };

        for (let colIdx = 1; colIdx <= worksheet.columns.length; colIdx++) {
          const cell = totalsRow.getCell(colIdx);
          cell.border = {
            top: { style: "thin", color: { argb: "FFB0B0B0" } },
            left: { style: "thin", color: { argb: "FFB0B0B0" } },
            bottom: { style: "double", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FFB0B0B0" } }
          };

          if ([11, 12, 13, 14].includes(colIdx)) {
            const letter = colLetter(colIdx);
            const dataStart = 2;
            const dataEnd = list.length + 1;
            cell.value = { formula: `SUM(${letter}${dataStart}:${letter}${dataEnd})` };
            cell.font = { name: "Calibri", size: 9, bold: true };
            cell.numFmt = "#,##0.00";
            cell.alignment = { vertical: "middle", horizontal: "right" };
          }
        }

        // Auto filter and views
        worksheet.autoFilter = {
          from: { row: 1, column: 1 },
          to: { row: 1, column: worksheet.columns.length }
        };

        worksheet.views = [
          {
            state: "frozen",
            xSplit: 2, // Freeze up to N° Documento
            ySplit: 1, // Freeze header row
            zoomScale: 100,
            showGridLines: true
          }
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Reporte_Adelantos_Productores_${new Date().getTime()}.xlsx`);
        this.$fun.alert("Archivo Excel generado y descargado correctamente.", "success");
      } catch (error) {
        console.error("Error generating excel:", error);
        this.$fun.alert("Ocurrió un error al generar el archivo Excel.", "error");
      }
    },
  },
};
</script>

<style scoped>
.bg-expanded-row {
  background-color: #f8f9fa !important;
}

.filter-card {
  background-color: #fbfcfc !important;
  border: 1px solid #e5e8e8 !important;
  border-radius: 8px;
}

.font-weight-semibold {
  font-weight: 600;
}
</style>
